/**
 * Programmatically refresh a DynamicTableV2 instance from outside the table.
 *
 * Dispatches a `dt-v2:refresh` CustomEvent. The table listens for this event
 * and matches against its `namespace` prop or `schema.metadata.entity`.
 *
 * When the target table is unmounted (e.g. user navigated to an edit form),
 * a pending refresh is stored so the table refetches on next mount.
 *
 * @param entity - The `metadata.entity` value from the table's schema (e.g. 'Department', 'Participant').
 *                 Pass `undefined` or omit to refresh ALL mounted tables.
 *
 * @example
 * // Refresh only the Department table
 * refreshTable('Department');
 *
 * // Refresh all mounted DynamicTableV2 instances
 * refreshTable();
 */
const PENDING_REFRESH_STORAGE_KEY = 'dt-v2:pendingRefresh';

type PendingRefreshMap = Record<string, number>;

function readPendingRefreshMap(): PendingRefreshMap {
  try {
    const raw = sessionStorage.getItem(PENDING_REFRESH_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingRefreshMap) : {};
  } catch {
    return {};
  }
}

function writePendingRefreshMap(map: PendingRefreshMap): void {
  sessionStorage.setItem(PENDING_REFRESH_STORAGE_KEY, JSON.stringify(map));
}

export function markPendingTableRefresh(entity: string): void {
  const map = readPendingRefreshMap();
  map[entity] = Date.now();
  writePendingRefreshMap(map);
}

export function consumePendingTableRefresh(entity: string): boolean {
  const map = readPendingRefreshMap();
  if (!(entity in map)) return false;
  delete map[entity];
  writePendingRefreshMap(map);
  return true;
}

export function clearPendingTableRefresh(entity: string): void {
  const map = readPendingRefreshMap();
  if (!(entity in map)) return;
  delete map[entity];
  writePendingRefreshMap(map);
}

export function refreshTable(entity?: string): void {
  if (entity != null) {
    markPendingTableRefresh(entity);
    window.dispatchEvent(new CustomEvent('dt-v2:refresh', { detail: { namespace: entity } }));
  } else {
    window.dispatchEvent(new CustomEvent('dt-v2:refresh'));
  }
}
